import React from 'react';
import { useQuery } from '@tanstack/react-query';
import {
  UsersIcon,
  PuzzlePieceIcon,
  CurrencyDollarIcon,
  WalletIcon,
  CpuChipIcon,
  BanknotesIcon,
  UserGroupIcon,
} from '@heroicons/react/24/outline';
import StatsCard from '../components/UI/StatsCard';
import RevenueTrend from '../components/dashboard/RevenueTrend';
import GameTrend from '../components/dashboard/GameTrend';
import UserTrend from '../components/dashboard/UserTrend';
import GameRooms from '../components/dashboard/GameRooms';
import Card from '../components/UI/Card';
import dashboardService from '../api/services/dashboard.service';

/* =========================================================
   HELPERS
========================================================= */

function fmtEtb(v: number): string {
  const n = Number.isFinite(v) ? v : 0;
  return `ETB ${n.toLocaleString(undefined, {
    minimumFractionDigits: 0,
    maximumFractionDigits: 2,
  })}`;
}

function pct(part: number, total: number): string {
  if (!total) return '—';
  return `${((part / total) * 100).toFixed(1)}%`;
}

/* =========================================================
   DASHBOARD
========================================================= */

const Dashboard: React.FC = () => {
  const {
    data: dashboardData,
    isLoading,
    error,
    refetch,
  } = useQuery({
    queryKey: ['dashboard'],
    queryFn: () => dashboardService.getDashboardData(),
    staleTime: 5 * 60 * 1000,
    retry: 1,
    refetchOnMount: false,
    refetchOnWindowFocus: false,
  });

  if (isLoading) {
    return (
      <div className="flex items-center justify-center h-96">
        <div className="text-center">
          <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-primary-500 mx-auto" />
          <p className="mt-4 text-gray-500">Loading dashboard...</p>
        </div>
      </div>
    );
  }

  if (error) {
    return (
      <div className="flex items-center justify-center h-96">
        <div className="text-center">
          <div className="text-red-500 text-4xl mb-4">⚠️</div>
          <p className="text-gray-700">
            {error instanceof Error
              ? error.message
              : 'Failed to load dashboard data'}
          </p>
          <button
            onClick={() => refetch()}
            className="mt-4 px-4 py-2 bg-primary-500 text-white rounded-lg hover:bg-primary-600"
          >
            Retry
          </button>
        </div>
      </div>
    );
  }

  if (!dashboardData) {
    return (
      <div className="flex items-center justify-center h-96">
        <p className="text-gray-500">No data available</p>
      </div>
    );
  }

  const { stats, revenueTrend, gameTrend, userTrend, gameRooms } =
    dashboardData;

  const topCards = [
    {
      title: 'Total Users',
      value: stats.totalUsers.toLocaleString(),
      todayValue: stats.todayUsers.toLocaleString(),
      icon: <UsersIcon className="w-5 h-5" />,
      color: 'primary' as const,
      showToday: true,
    },
    {
      title: 'Total Games',
      value: stats.totalGames.toLocaleString(),
      todayValue: stats.todayGames.toLocaleString(),
      icon: <PuzzlePieceIcon className="w-5 h-5" />,
      color: 'secondary' as const,
      showToday: true,
    },
    {
      title: 'Real Revenue',
      value: fmtEtb(stats.totalRevenue),
      todayValue: fmtEtb(stats.todayRevenue),
      icon: <CurrencyDollarIcon className="w-5 h-5" />,
      color: 'success' as const,
      showToday: true,
    },
    {
      title: 'Users Balance',
      value: fmtEtb(stats.usersRemainingBalance),
      todayValue: undefined,
      icon: <WalletIcon className="w-5 h-5" />,
      color: 'warning' as const,
      showToday: false,
    },
    // ⭐ NEW: Daily Unique Customers (today only, humans only)
    {
      title: 'Daily Unique Customers',
      value: stats.todayUniqueCustomers.toLocaleString(),
      todayValue: undefined,
      icon: <UserGroupIcon className="w-5 h-5" />,
      color: 'primary' as const,
      showToday: false,
    },
  ];

  const gamesAiShare = pct(stats.gamesWithAi, stats.totalGames);
  const todayGamesAiShare =
    stats.todayGames > 0 ? pct(stats.todayGamesWithAi, stats.todayGames) : '—';

  const aiWinRate = pct(stats.gamesAiWon, stats.gamesWithAi);
  const todayAiWinRate =
    stats.todayGamesWithAi > 0
      ? pct(stats.todayGamesAiWon, stats.todayGamesWithAi)
      : '—';

  const payoutRatio = pct(stats.totalRealPayoutOut, stats.totalRealStakeIn);
  const todayPayoutRatio =
    stats.todayRealStakeIn > 0
      ? pct(stats.todayRealPayoutOut, stats.todayRealStakeIn)
      : '—';

  return (
    <div>
      <div className="mb-6">
        <h1 className="text-2xl font-bold text-gray-900">Dashboard</h1>
        <p className="text-sm text-gray-500 mt-0.5">
          Welcome back! Here's what's happening with your business.
        </p>
      </div>

      {/* ── 5 headline cards ─────────────────────── */}
      <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-5 gap-4 mb-6">
        {topCards.map((stat, index) => (
          <StatsCard
            key={index}
            title={stat.title}
            value={stat.value}
            icon={stat.icon}
            todayValue={stat.todayValue}
            color={stat.color}
            showToday={stat.showToday}
          />
        ))}
      </div>

      {/* ── 2 grouped cards ──────────────────────── */}
      <div className="grid grid-cols-1 lg:grid-cols-2 gap-4 mb-6">
        {/* AI Summary */}
        <Card>
          <div className="flex items-center justify-between mb-4">
            <div className="flex items-center gap-2">
              <CpuChipIcon className="w-5 h-5 text-gray-900" />
              <h2 className="text-sm font-bold text-gray-900">AI Activity</h2>
            </div>
            <span className="text-[11px] font-medium text-gray-500">
              Today in brackets
            </span>
          </div>

          <div className="grid grid-cols-1 md:grid-cols-3 gap-3">
            <MiniStat
              label="Games With AI"
              value={stats.gamesWithAi.toLocaleString()}
              inline={gamesAiShare !== '—' ? `(${gamesAiShare})` : undefined}
              today={stats.todayGamesWithAi.toLocaleString()}
              todayInline={
                todayGamesAiShare !== '—' ? `(${todayGamesAiShare})` : undefined
              }
            />
            <MiniStat
              label="Games Without AI"
              value={stats.gamesWithoutAi.toLocaleString()}
              today={stats.todayGamesWithoutAi.toLocaleString()}
            />
            <MiniStat
              label="Games AI Won"
              value={stats.gamesAiWon.toLocaleString()}
              inline={aiWinRate !== '—' ? `(${aiWinRate})` : undefined}
              today={stats.todayGamesAiWon.toLocaleString()}
              todayInline={
                todayAiWinRate !== '—' ? `(${todayAiWinRate})` : undefined
              }
              tone="warm"
            />
          </div>
        </Card>

        {/* Revenue Detail */}
        <Card>
          <div className="flex items-center justify-between mb-4">
            <div className="flex items-center gap-2">
              <BanknotesIcon className="w-5 h-5 text-gray-900" />
              <h2 className="text-sm font-bold text-gray-900">
                Real Revenue Detail
              </h2>
            </div>
            <span className="text-[11px] font-medium text-gray-500">
              Today in brackets
            </span>
          </div>

          <div className="grid grid-cols-1 md:grid-cols-3 gap-3">
            <MiniStat
              label="Stake In"
              value={fmtEtb(stats.totalRealStakeIn)}
              today={fmtEtb(stats.todayRealStakeIn)}
              tone="green"
            />
            <MiniStat
              label="Payout Out"
              value={fmtEtb(stats.totalRealPayoutOut)}
              inline={payoutRatio !== '—' ? `(${payoutRatio})` : undefined}
              today={fmtEtb(stats.todayRealPayoutOut)}
              todayInline={
                todayPayoutRatio !== '—' ? `(${todayPayoutRatio})` : undefined
              }
              tone="warm"
            />
            <MiniStat
              label="Net Revenue"
              value={fmtEtb(stats.totalRevenue)}
              today={fmtEtb(stats.todayRevenue)}
              tone={stats.totalRevenue >= 0 ? 'green' : 'warm'}
              bold
            />
          </div>
        </Card>
      </div>

      {/* ── Charts ────────────────────────────────── */}
      <div className="grid grid-cols-1 lg:grid-cols-3 gap-4 mb-6">
        <div className="lg:col-span-2">
          <RevenueTrend monthlyData={revenueTrend} />
        </div>
        <div>
          <GameRooms rooms={gameRooms} />
        </div>
      </div>

      <div className="space-y-4">
        <GameTrend trendData={gameTrend} />
        <UserTrend trendData={userTrend} />
      </div>
    </div>
  );
};

/* =========================================================
   MINI STAT — larger today line
========================================================= */

const MiniStat: React.FC<{
  label: string;
  value: string | number;
  inline?: string;
  today?: string | number;
  todayInline?: string;
  tone?: 'green' | 'warm' | 'neutral';
  bold?: boolean;
}> = ({ label, value, inline, today, todayInline, tone, bold }) => {
  const valueColor =
    tone === 'green'
      ? 'text-green-700'
      : tone === 'warm'
      ? 'text-red-600'
      : 'text-gray-900';

  return (
    <div className="p-3 rounded-lg border border-gray-100 bg-gray-50/40">
      <p className="text-[11px] uppercase text-gray-500 tracking-wide font-semibold">
        {label}
      </p>

      <p
        className={`mt-1 ${bold ? 'text-xl' : 'text-lg'} font-bold ${valueColor}`}
      >
        {value}
        {inline && (
          <span className="ml-1.5 text-sm font-semibold opacity-80">
            {inline}
          </span>
        )}
      </p>

      {(today !== undefined || todayInline) && (
        <p className="mt-1 text-sm font-medium text-gray-600">
          Today: <span className="font-semibold text-gray-900">{today}</span>
          {todayInline && (
            <span className="ml-1 font-semibold text-gray-700">
              {todayInline}
            </span>
          )}
        </p>
      )}
    </div>
  );
};

export default Dashboard;